import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import "@/App.css";
import { LanguageProvider } from "@/i18n/LanguageContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BackButton from "@/components/BackButton";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import Training from "@/pages/Training";
import CourseDetail from "@/pages/CourseDetail";
import Experts from "@/pages/Experts";
import ExpertProfile from "@/pages/ExpertProfile";
import ExpertBook from "@/pages/ExpertBook";
import ExpertPortal from "@/pages/ExpertPortal";
import ClientPortal from "@/pages/ClientPortal";
import ResearchConsultants from "@/pages/ResearchConsultants";
import Join from "@/pages/Join";
import Insights from "@/pages/Insights";
import InsightDetail from "@/pages/InsightDetail";
import Videos from "@/pages/Videos";
import Contact from "@/pages/Contact";
import Consultation from "@/pages/Consultation";
import { PrivacyPage, TermsPage } from "@/pages/Legal";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";

function ScrollManager() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function PublicLayout({ children }) {
  return (
    <>
      <Navbar />
      <main>{children}</main>
      <Footer />
    </>
  );
}

export default function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, wheelMultiplier: 1 });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <LanguageProvider>
      <BrowserRouter>
        <ScrollManager />
        <BackButton />
        <Toaster richColors position="top-center" />
        <Routes>
          <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
          <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
          <Route path="/services" element={<PublicLayout><Services /></PublicLayout>} />
          <Route path="/training" element={<PublicLayout><Training /></PublicLayout>} />
          <Route path="/training/:id" element={<PublicLayout><CourseDetail /></PublicLayout>} />
          <Route path="/experts" element={<PublicLayout><Experts /></PublicLayout>} />
          <Route path="/experts/:id/book" element={<PublicLayout><ExpertBook /></PublicLayout>} />
          <Route path="/experts/:id" element={<PublicLayout><ExpertProfile /></PublicLayout>} />
          <Route path="/expert-portal" element={<ExpertPortal />} />
          <Route path="/client-portal" element={<ClientPortal />} />
          <Route path="/research-consultants" element={<PublicLayout><ResearchConsultants /></PublicLayout>} />
          <Route path="/join" element={<PublicLayout><Join /></PublicLayout>} />
          <Route path="/insights" element={<PublicLayout><Insights /></PublicLayout>} />
          <Route path="/insights/:slug" element={<PublicLayout><InsightDetail /></PublicLayout>} />
          <Route path="/videos" element={<PublicLayout><Videos /></PublicLayout>} />
          <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
          <Route path="/consultation" element={<PublicLayout><Consultation /></PublicLayout>} />
          <Route path="/privacy" element={<PublicLayout><PrivacyPage /></PublicLayout>} />
          <Route path="/terms" element={<PublicLayout><TermsPage /></PublicLayout>} />
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="*" element={<PublicLayout><NotFound /></PublicLayout>} />
        </Routes>
      </BrowserRouter>
    </LanguageProvider>
  );
}

function NotFound() {
  return (
    <div className="mx-auto max-w-7xl px-4 py-32 text-center" data-testid="not-found-page">
      <h1 className="font-display text-5xl font-semibold text-navy">404</h1>
      <p className="mt-4 text-slate-600">Page not found</p>
      <a href="/" className="mt-8 inline-block rounded-full bg-gold px-6 py-3 text-sm font-bold text-navy">
        Return home
      </a>
    </div>
  );
}
